import { Container, Grid, Typography, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ErrorIcon } from '../../../../icons/ErrorIcon';
import { QualityScore } from '../Quality';
import { useAppStateContext } from '../../../AppStateProvider/AppStateProvider';
import { ViewIcon } from '../../../../icons/ViewIcon';
import { WarningIcon } from '../../../../icons/WarningIcon';

const Header = styled(Grid)(({ theme }) => ({
  float: 'left',
  [theme.breakpoints.down('md')]: {
    float: 'initial',
  },
}));

const Heading = styled(Typography)({
  position: 'relative',
});

const StyledPaper = styled(Paper)({
  padding: '1.2em',
  maxWidth: '400px',
  borderRadius: '8px',
  '& li': {
    '&:not(:last-child)': {
      marginBottom: '1.5em',
    },
  },
});

const PaperContainer = styled(Grid)(({ theme }) => ({
  float: 'right',
  marginRight: '1em',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    float: 'initial',
    justifyContent: 'center',
    margin: '0 0 2.5em 0',
  },
}));

const ViewButton = styled(Button)({
  marginTop: '2em',
  '& svg': {
    position: 'relative',
    left: '-5px',
  },
});

const ConfirmationButtons = styled(Grid)(({ theme }) => ({
  clear: 'left',
  [theme.breakpoints.down('md')]: {
    clear: 'initial',
    marginBottom: '2em',
  },
}));

interface PoorQualityProps {
  quality: QualityScore | undefined;
  openModal: () => void;
}

export function PoorQuality({ quality, openModal }: PoorQualityProps) {
  const { nextPane } = useAppStateContext();

  return (
    <Container>
      <div>
        <Header item lg={5}>
          <Heading variant="h1" gutterBottom>
            {quality === QualityScore.Suboptimal ? <WarningIcon /> : <ErrorIcon />}
            Quality {quality === QualityScore.Suboptimal ? 'is okay' : 'issues'}
          </Heading>

          <Typography variant="body1" gutterBottom>
            This the last step! Your expected audio and video quality is{' '}
            <strong>{quality === QualityScore.Suboptimal ? 'suboptimal' : 'degraded'} </strong>
            and overall performance {quality === QualityScore.Suboptimal ? 'could be better' : 'will be poor'}.
          </Typography>
        </Header>

        <PaperContainer item lg={5}>
          <StyledPaper>
            <Typography variant="body1">
              <strong>Tips to improve quality and performance:</strong>
            </Typography>

            <ul>
              <li>
                <Typography variant="body1">
                  Close unused tabs and applications on your computer/device when using video.
                </Typography>
              </li>
              <li>
                <Typography variant="body1">Move closer to your router.</Typography>
              </li>
              <li>
                <Typography variant="body1">Turn off or disconnect from VPN.</Typography>
              </li>
              <li>
                <Typography variant="body1">Restart your computer or device.</Typography>
              </li>
            </ul>
          </StyledPaper>

          <ViewButton variant="outlined" onClick={openModal}>
            <ViewIcon />
            View detailed quality information
          </ViewButton>
        </PaperContainer>

        <ConfirmationButtons item lg={5}>
          <Typography variant="body1" gutterBottom>
            <strong>Did you try all of the tips to improve quality?</strong>
          </Typography>

          <Button variant="contained" color="primary" onClick={nextPane} style={{ marginRight: '1.5em' }}>
            Yes
          </Button>
          <Button color="primary" onClick={nextPane}>
            Skip for now
          </Button>
        </ConfirmationButtons>
      </div>
    </Container>
  );
}
